// ---------------------------------------------------------------------------
// INB-014: Inbox Auto-Replies + Away-Hours
// ---------------------------------------------------------------------------
// Per-tenant auto-reply rules for inbound inbox messages. Three trigger types:
//   - away_hours : fire when current time is outside configured business hours
//   - keyword    : fire when an inbound message body contains a keyword
//   - new_thread : fire once per newly created conversation
//
// A rule points at a `cannedResponses` template (`templateId`) which provides
// the body to send. Per-conversation/per-rule idempotency is enforced at the
// engine layer via `inbox_auto_reply_deliveries`; rules are otherwise stateless
// configuration.

import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  jsonb,
  timestamp,
  index,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

/** Trigger types for inbox auto-replies. */
export const INBOX_AUTO_REPLY_TRIGGER_TYPES = [
  "away_hours",
  "keyword",
  "new_thread",
] as const;
export type InboxAutoReplyTriggerType =
  (typeof INBOX_AUTO_REPLY_TRIGGER_TYPES)[number];

/** Channel filter — empty array means "all channels". */
export type InboxAutoReplyChannels = string[];

/** Keyword list for `keyword` trigger. Case-insensitive substring match. */
export type InboxAutoReplyKeywords = string[];

export const inboxAutoReplies = pgTable(
  "inbox_auto_replies",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    triggerType: text("trigger_type").notNull(), // away_hours | keyword | new_thread
    templateId: uuid("template_id").notNull(), // FK -> canned_responses.id
    // Away-hours configuration (only meaningful for `away_hours` trigger).
    // Stored as "HH:MM" strings in the tenant's IANA `timezone`. A rule is
    // "away" when current local time is OUTSIDE [start, end). Wrap-around
    // (e.g. 18:00 -> 09:00) is supported and represents overnight away-hours.
    awayHoursStart: text("away_hours_start"), // "HH:MM" or null
    awayHoursEnd: text("away_hours_end"), // "HH:MM" or null
    timezone: text("timezone").default("UTC").notNull(), // IANA tz
    keywords: jsonb("keywords").$type<InboxAutoReplyKeywords>().default([]).notNull(),
    channels: jsonb("channels").$type<InboxAutoReplyChannels>().default([]).notNull(),
    enabled: boolean("enabled").default(true).notNull(),
    sendCount: integer("send_count").default(0).notNull(), // analytics
    createdBy: text("created_by").notNull(), // userId
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_inbox_auto_replies_tenant").on(table.tenantId),
    index("idx_inbox_auto_replies_tenant_enabled").on(
      table.tenantId,
      table.enabled,
    ),
    index("idx_inbox_auto_replies_tenant_trigger").on(
      table.tenantId,
      table.triggerType,
    ),
  ],
);

/**
 * Idempotency ledger — one row per (rule, conversation). Prevents the same
 * rule from firing twice on the same thread. Engine MUST insert with
 * `ON CONFLICT DO NOTHING` and skip sending when no row is returned. The
 * uniqueness constraint is enforced by a partial unique index added in the
 * migration so callers can rely on `ON CONFLICT (rule_id, conversation_id)`.
 */
export const inboxAutoReplyDeliveries = pgTable(
  "inbox_auto_reply_deliveries",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    ruleId: uuid("rule_id").notNull(),
    conversationId: uuid("conversation_id").notNull(),
    sentAt: timestamp("sent_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_inbox_auto_reply_deliveries_tenant").on(table.tenantId),
    index("idx_inbox_auto_reply_deliveries_rule_conv").on(
      table.ruleId,
      table.conversationId,
    ),
  ],
);
